import React from 'react';
import { Button, Modal } from '@a_team/ui-components';
import { createUseStyles } from 'react-jss';
import useMediaQuery from '@src/hooks/useMediaQuery';
import { Breakpoints, TextColors } from '@ateams/components';
import { useStores } from '@src/stores';
import GradientBox from '../GradientBox';

interface ReleaseConnectionModalProps {
  onClose: () => void;
  setIsReleaseStepsModalOpen: (force?: unknown) => void;
  shouldSetWhatsNewModalAsDisplayed?: boolean;
  isOpen: boolean;
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    color: TextColors.regular,
    textAlign: 'center',
    flexDirection: 'column',
  },
  content: {
    padding: 40,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButton: {
    backgroundColor: 'transparent !important',
    color: 'black !important',
  },
  imageContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    '& > img': {
      borderRadius: '16px 16px 0px 0px',
      aspectRatio: '1.3',
    },
  },
  title: {
    fontSize: 32,
    letterSpacing: '0.001em',
    lineHeight: '120%',
    margin: '16px 0',
  },
  desc: {
    margin: '0 0 40px 0',
  },

  modal: {
    '&&': {
      width: '100%',
      maxWidth: '100%',
      borderRadius: '8px !important',
      '& > div': {
        padding: '0 !important',
      },
    },
  },
  [`@media (min-width: ${Breakpoints.md}px)`]: {
    container: {
      flexDirection: 'row-reverse',
    },
    imageContainer: {
      maxWidth: '425px',
      width: '100%',
      '& > img': {
        aspectRatio: '0.94',
        borderRadius: '0 8px 8px 0',
      },
    },
    modal: {
      '&&': {
        width: '850px !important',
        maxWidth: '850px',
        padding: '0 !important',
      },
      '& > div': {
        padding: '0 !important',
      },
      '& > div:last-of-type': {
        display: 'none',
      },
    },
  },
});

const mobileImage = `https://cdn.a.team/website/connection-release-c-mobile.jpg`;
const desktopImage = `https://cdn.a.team/website/connection-release-c.jpg`;

const ReleaseConnectionModal = ({
  onClose,
  isOpen,
  shouldSetWhatsNewModalAsDisplayed,
  setIsReleaseStepsModalOpen,
}: ReleaseConnectionModalProps): JSX.Element => {
  const styles = useStyles();
  const isDesktop = useMediaQuery(`(min-width: ${Breakpoints.md}px)`);

  const { auth } = useStores();

  const handleCTA = async () => {
    if (!shouldSetWhatsNewModalAsDisplayed) {
      onClose();
      setIsReleaseStepsModalOpen(true);
      return;
    }
    try {
      await auth.setWhatsNewModalAsDisplayed(auth);
    } catch (e) {
      console.error(e);
    } finally {
      onClose();
      setIsReleaseStepsModalOpen(true);
    }
  };

  const handleOnClose = async () => {
    if (!shouldSetWhatsNewModalAsDisplayed) {
      onClose();
      return;
    }
    try {
      await auth.setWhatsNewModalAsDisplayed(auth);
    } catch (e) {
      console.error(e);
    } finally {
      onClose();
    }
  };

  return (
    <Modal
      className={styles.modal}
      hideCloseButton
      variant="slideUp"
      isOpen={isOpen}
      onClose={handleOnClose}
    >
      <div className={styles.container}>
        <div className={styles.imageContainer}>
          <img
            src={isDesktop ? desktopImage : mobileImage}
            alt="New Connections"
            width="100%"
          />
        </div>
        <div className={styles.content}>
          <GradientBox>Connections</GradientBox>
          <h2 className={styles.title}>
            Put your network <br /> to work
          </h2>
          <p className={styles.desc}>
            Form new connections. Land more missions.
          </p>
          <Button size="md" onClick={handleCTA}>
            See what’s new
          </Button>
          <Button
            size="md"
            className={styles.closeButton}
            onClick={handleOnClose}
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReleaseConnectionModal;
